import cn from 'classnames';
import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';

import {
  getPixelTrackers,
  trackPixelProgress,
} from '../../../../utils/pixelTrackers';
import { useTranslation } from '../../../../utils/useTranslation';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { usePlayerEvent } from '../../hooks/usePlayerEvent';
import { usePulse } from '../../hooks/PulseProvider';
import { useProvider } from '../../hooks/useProvider';
import { useTpaasTracker } from '../../hooks/useTpaasTracker';
import ns from '../../translations';
import { hasEditorialLink, hasSponsorData } from '../../utils/assetHelpers';
import { AdLink } from '../AdLink/AdLink';
import { getOverlayData } from '../../utils/sponsorshipOverlay';
import { ButtonProps } from '../Button/Button';

export const SponsorLink: FunctionComponent<{
  size?: ButtonProps['size'];
  fontSize?: 'small' | 'medium' | 'large';
}> = ({ size, fontSize }) => {
  const { asset } = useCurrentAsset();
  const { assetProvider } = useProvider();
  const { trackMediaEvent } = usePulse();
  const { trackEngagementUIElement } = useTpaasTracker();
  const { t } = useTranslation(ns);
  const pixelTrackersRef = useRef<ReturnType<typeof getPixelTrackers>>();

  const linkData = useMemo(() => {
    if (!asset) return undefined;
    return getOverlayData(asset);
  }, [asset]);

  useEffect(() => {
    if (asset) {
      pixelTrackersRef.current = getPixelTrackers(asset);
    }
  }, [asset]);

  const handleTime = useCallback(
    (currentTimeInSeconds: number, totalTimeInSeconds: number) => {
      if (!asset || (!hasSponsorData(asset) && !hasEditorialLink(asset))) {
        return;
      }

      if (pixelTrackersRef.current) {
        trackPixelProgress(
          pixelTrackersRef.current,
          currentTimeInSeconds,
          totalTimeInSeconds
        );
      }
    },
    [asset]
  );

  const handleComplete = useCallback(() => {
    if (pixelTrackersRef.current) {
      pixelTrackersRef.current.track100();
    }
  }, []);

  usePlayerEvent('time', handleTime);
  usePlayerEvent('complete', handleComplete);

  const handleClick = useCallback(() => {
    if (!linkData || !asset) {
      return;
    }

    const brand = assetProvider === 'ab' ? 'aftonbladet' : 'vgtv';

    trackMediaEvent({
      '@type': 'Engagement',
      'action': 'Click',
      'object': {
        '@id': `sdrn:${brand}:article:${asset.id}:element:${asset.id}`,
        '@type': 'UIElement',
        'spt:custom': {
          'rel': 'noopener',
          'spt:is_sponsored': linkData.type === 'sponsored',
          ...(linkData.type === 'sponsored'
            ? { sponsor: linkData.sponsor }
            : {}),
        },
      },
      'target': {
        url: linkData.url,
      },
    });

    trackEngagementUIElement({
      elementName:
        linkData.type === 'sponsored' ? 'SponsorLink' : 'EditorialLink',
      elementType: 'a',
      name: linkData.type === 'sponsored' ? 'Sponsor link' : 'Editorial link',
      targetUrl: linkData.url,
    });
  }, [
    linkData,
    asset,
    assetProvider,
    trackMediaEvent,
    trackEngagementUIElement,
  ]);

  if (!linkData) {
    return null;
  }

  const adLabel = linkData.type === 'sponsored' ? t('advertisement') : '';

  const adText =
    linkData.type === 'sponsored' ? linkData.text || t('See more') : '';

  return (
    <div
      className={cn('vgp-sponsor-link', {
        'vgp-sponsor-link--editorial': linkData.type === 'editorial',
      })}
    >
      <AdLink
        url={linkData.url}
        label={adLabel || linkData.text}
        text={adText}
        handleAdLinkClick={handleClick}
        className="vgp-sponsor-link__link"
        size={size}
        fontSize={fontSize}
      />
    </div>
  );
};
